import type { BreakpointType } from '@gik/core/hooks/CustomHooks';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import type { Group } from '@gik/core/models/gik/Group';
import { useCan } from '@gik/core/store/permissions.ts';
import type { UIComponent } from '@gik/core/types/UI';
import { navigateTo } from '@gik/core/utils/navigateTo.ts';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { IconButtonCircle, UI, cn } from '@gik/ui';
import { HTMLParser } from '@gik/ui-legacy/HTMLParser/HTMLParser';
import he from 'he';
import classMap from '../styles/classMap.ts';

export type InkindGroupHeroProps = {
  group: Group;
  logoImageUrl?: string;
  muted?: boolean;
} & UIComponent &
  BreakpointType;

function InkindGroupHeroComp(props: InkindGroupHeroProps) {
  const { group } = props;

  // The server stores the group name HTML-encoded (e.g. an apostrophe arrives
  // as `&#8217;`). Rendered directly as JSX text it would show the literal
  // entity, so decode it the same way every other group-title render site does.
  const title = group?.title ? he.decode(group.title).trim() : undefined;
  const description = group?.inkindGroupSettings?.description?.trim();

  const canManageGroup = useCan('manage', 'groups', { groupId: group?.id }) && !!group;

  // `useCan` resolves from a client-only permissions store, so it is always
  // `false` on the server and the client's first (hydrating) render — then
  // flips to its real value once the store has populated after mount. Any
  // markup gated on it (the admin-only edit buttons below) must therefore
  // also be gated behind `mounted()`, or the client's first render can already
  // show a button the server never produced, causing a hydration mismatch.
  // Same pattern as `WishlistSidebarSection`'s edit/add buttons.
  const mounted = useIsMounted();
  const isMounted = mounted();
  const showEditSettingsButton = canManageGroup && isMounted;

  // Admins can hide the group name from the public Group Page via the
  // "Show Group Name on Page" toggle (Info & Privacy settings). Defaults to
  // shown when the backend hasn't stored a preference yet.
  const showName = group?.inkindGroupSettings?.showNameOnPage !== false;

  // The group name is optional and can be hidden, so the public title may be
  // absent. Admins must still always get an entry point to manage it, mirroring
  // the always-editable description section below.
  const publiclyVisibleTitle = showName && !!title;

  const editSettingsButton = showEditSettingsButton ? (
    <IconButtonCircle
      onClick={() => group.id && navigateTo(`/groups/${group.wordPressGroupSlug}/admin/settings`)}
      size={UI.Size.LG}
      color={UI.Color.White}
      elevation={UI.Elevation.SM}
      iconColor={UI.Color.Secondary}
      iconSize={'20px'}
      name="edit"
    />
  ) : null;

  return (
    <div className={classMap.groupHeroRoot()}>
      {/* Temporarily hidden: to be enabled later */}
      {/* <div className={classMap.groupHeroShareRow()}>
        <div className={classMap.groupHeroShareButtonWrap()}>
          <ShareGroupButton groupId={group.id} variant={'primary'} />
        </div>
      </div> */}

      {/* Title:
          - Public visitors see the name only when it is set AND the
            "Show Group Name on Page" toggle is on.
          - Admins always get an editable affordance so they can set, edit, or
            re-show an empty/hidden group name. */}
      {publiclyVisibleTitle ? (
        <div className="text-center">
          <div className="inline-flex justify-center items-center gap-2  mt-4">
            <span className={cn(classMap.groupHeroTitle(), 'max-w-[685px] mx-auto')}>{title}</span>
            {editSettingsButton}
          </div>
        </div>
      ) : showEditSettingsButton ? (
        // Reached only when the name is hidden ("Show Group Name on Page" off)
        // or unset — branch above already handles the public title. Either way
        // admins get the empty placeholder as an entry point to set/re-show it.
        <div className={cn(classMap.groupHeroCard(), 'font-heading font-semibold text-2xl ')}>
          <span>Show your Group Name here</span>
          {editSettingsButton}
        </div>
      ) : null}

      {description ? (
        <div className={cn(classMap.groupHeroDescription(), 'max-w-[685px] mx-auto')}>
          <div className={cn(classMap.groupHeroDescriptionWrapper(), 'group-hero-description')}>
            <HTMLParser rawHtml={description} />
            {/* The description itself is public; only admins get the edit affordance. */}
            {showEditSettingsButton && (
              <>
                &nbsp;&nbsp;
                <IconButtonCircle
                  onClick={() => group.id && navigateTo(`/groups/${group.wordPressGroupSlug}/admin/settings`)}
                  size={UI.Size.LG}
                  color={UI.Color.White}
                  elevation={UI.Elevation.SM}
                  iconColor={UI.Color.Secondary}
                  iconSize={'20px'}
                  name="edit"
                  style={{ position: 'relative', top: '2px' }}
                />
              </>
            )}
          </div>
        </div>
      ) : showEditSettingsButton ? (
        // Empty-state placeholder is an editing affordance — only show it to
        // admins who can actually add a description.
        <div className={cn(classMap.groupHeroCard(), 'text-md')}>
          <span>Add your group description here</span>
          <IconButtonCircle
            onClick={() => group.id && navigateTo(`/groups/${group.wordPressGroupSlug}/admin/settings`)}
            size={UI.Size.LG}
            color={UI.Color.White}
            elevation={UI.Elevation.SM}
            iconColor={UI.Color.Secondary}
            iconSize={'20px'}
            name="edit"
          />
        </div>
      ) : null}
    </div>
  );
}

export const InkindGroupHero = withComponentErrorBoundary(InkindGroupHeroComp);
